<template>
  <div class="console-card img-panel">
    <div>
      <h3 class="img-title">Replace one image</h3>
      <p class="img-hint">Swap a slice for your own file, an animated one for example. Pick the file, then the image it replaces.</p>
    </div>

    <label class="field">
      <span>Replacement file</span>
      <input
        type="file"
        class="img-file"
        accept="image/gif,image/jpeg,image/png,image/webp"
        @change="handleFileSelection"
      >
      <small v-if="selectedFile">{{ selectedFile.name }}, {{ formatFileSize(selectedFile.size) }}</small>
    </label>

    <div v-if="email && displaySections.length > 0">
      <div class="console-label" style="margin-bottom:6px;">Image to replace</div>
      <div class="flex flex-col gap-3">
        <template v-for="(section, sIndex) in displaySections" :key="`section-${sIndex}`">
          <div v-if="section.type === 'image' && section.cells && section.cells.length > 0">
            <div style="color:var(--muted); font-size:13px; margin-bottom:4px;">Section {{ sIndex + 1 }}</div>
            <div class="flex flex-wrap gap-2">
              <button
                v-for="(image, iIndex) in section.cells"
                :key="`image-${sIndex}-${iIndex}`"
                type="button"
                class="img-pick"
                :class="{ on: isSelected(sIndex, iIndex) }"
                :aria-pressed="isSelected(sIndex, iIndex)"
                @click="selectImage(sIndex, iIndex)"
              >
                <span style="font-weight:600;">Image {{ iIndex + 1 }}</span>
                <span class="truncate" style="color:var(--muted); font-size:13px;">{{ getImageFilename(image.src) }}</span>
              </button>
            </div>
          </div>
        </template>
      </div>
    </div>

    <div class="flex flex-wrap justify-end gap-2">
      <AppButton @click="resetForm">Clear</AppButton>
      <AppButton variant="primary" :disabled="!canUpload || uploading" @click="uploadReplacement">
        {{ uploading ? 'Replacing…' : 'Replace image' }}
      </AppButton>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue'
import type { PropType } from 'vue'
import { normalizeSections } from '~/composables/useEmailSections'

const props = defineProps({
  email: {
    type: Object as PropType<EmailT>,
    required: true
  },
  groupAlias: {
    type: String,
    required: true
  },
  emailAlias: {
    type: String,
    required: true
  }
})

const emit = defineEmits(['update:email', 'image-swapped'])

// State
const selectedFile = ref<File | null>(null)
const selectedSection = ref<number | null>(null)
const selectedImage = ref<number | null>(null)
const uploading = ref(false)

// Normalized (typed) view of the email's sections so cell indices stay stable.
const displaySections = computed(() => normalizeSections(props.email.sections))

// Computed
const canUpload = computed(() =>
  selectedFile.value !== null &&
  selectedSection.value !== null &&
  selectedImage.value !== null
)

// Helper functions
function formatFileSize(bytes: number): string {
  if (bytes < 1024) return bytes + ' B'
  else if (bytes < 1048576) return (bytes / 1024).toFixed(1) + ' KB'
  else return (bytes / 1048576).toFixed(1) + ' MB'
}

function getImageFilename(src: string): string {
  // Extract filename from path, handling $SIZE placeholder
  if (src.includes('$SIZE')) {
    return src.split('/').pop() || src
  }
  return src.split('/').pop() || src
}

function isSelected(sectionIndex: number, imageIndex: number): boolean {
  return selectedSection.value === sectionIndex && selectedImage.value === imageIndex
}

// Event handlers
function handleFileSelection(event: Event): void {
  const input = event.target as HTMLInputElement
  if (input.files && input.files.length > 0) {
    selectedFile.value = input.files[0]
  }
}

function selectImage(sectionIndex: number, imageIndex: number): void {
  selectedSection.value = sectionIndex
  selectedImage.value = imageIndex
}

function resetForm(): void {
  selectedFile.value = null
  selectedSection.value = null
  selectedImage.value = null
}

async function uploadReplacement(): Promise<void> {
  if (!canUpload.value || !props.email) return

  try {
    uploading.value = true

    // Create FormData with the file
    const formData = new FormData()
    formData.append('images', selectedFile.value as File)

    // Upload to server endpoint
    const response = await $fetch(`/api/v1/images/${props.groupAlias}/${props.emailAlias}/upload`, {
      method: 'POST',
      body: formData
    })

    if (response.success && response.files.length > 0) {
      const sIdx = selectedSection.value as number
      const iIdx = selectedImage.value as number
      const originalImage = displaySections.value[sIdx]!.cells![iIdx]!
      const newFilename = response.files[0].originalName

      // Clone + normalize so we mutate typed image-band cells (lazy migration on save).
      const updatedEmail = JSON.parse(JSON.stringify(props.email))
      updatedEmail.sections = normalizeSections(updatedEmail.sections)
      const targetCell = updatedEmail.sections[sIdx].cells[iIdx]

      // Store if the image uses $SIZE pattern
      const usesSizePattern = originalImage.src.includes('$SIZE')

      if (usesSizePattern) {
        // For responsive images with $SIZE pattern keep the pattern, update the base filename.
        targetCell.src = originalImage.src.replace(
          /(\$SIZE_.*)\..*$/,
          `$SIZE_${newFilename.split('.')[0]}.${newFilename.split('.')[1]}`,
        )
      } else {
        // For simple images, just replace the filename
        targetCell.src = newFilename
      }

      // Emit update event with the modified email
      emit('update:email', updatedEmail)
      emit('image-swapped', {
        sectionIndex: sIdx,
        imageIndex: iIdx,
        originalSrc: originalImage.src,
        newSrc: targetCell.src,
      })

      // Reset form
      resetForm()

      // Show success notification
      useToast().add({
        title: 'Image Replaced',
        description: 'Image has been successfully replaced',
        color: 'success'
      })
    }
  } catch (error) {
    console.error('Error uploading replacement image:', error)
    useToast().add({
      title: 'Error',
      description: error instanceof Error ? error.message : 'Failed to replace image',
      color: 'error'
    })
  } finally {
    uploading.value = false
  }
}
</script>

<style scoped>
.img-file {
  color: var(--muted);
  font-size: 13px;
}
.img-file::file-selector-button {
  height: 32px;
  margin-right: 10px;
  padding: 0 14px;
  border: 1px solid var(--line);
  border-radius: 999px;
  background: var(--panel);
  color: var(--ink);
  font: inherit;
  font-weight: 600;
  cursor: pointer;
}
.img-pick {
  display: flex;
  flex-direction: column;
  flex: 1 1 160px;
  gap: 2px;
  min-width: 0;
  padding: 10px 12px;
  border: 1px solid var(--line2);
  border-radius: 10px;
  background: transparent;
  color: var(--ink);
  text-align: left;
  cursor: pointer;
}
.img-pick:hover {
  background: var(--panel2);
}
.img-pick.on {
  border-color: var(--accentline);
  background: var(--panel2);
}
</style>
